import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Upload, Award, TrendingUp, Clock, Users, Shield, Loader2, FileText, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SectionBubbles from '../components/SectionBubbles';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Careers = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    city: '',
    position: '',
    availability: '',
    experience: '',
    message: ''
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // 1. Submit application
      const res = await fetch(`${API_URL}/api/careers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          city: formData.city,
          position: formData.position,
          availability: formData.availability,
          experience: formData.experience,
          message: formData.message
        })
      });
      if (!res.ok) throw new Error('Submission failed');
      const application = await res.json();

      // 2. Upload resume if provided
      if (resumeFile) {
        const formPayload = new FormData();
        formPayload.append('application_id', application.id);
        formPayload.append('resume', resumeFile);
        const uploadRes = await fetch(`${API_URL}/api/careers/upload-resume`, {
          method: 'POST',
          body: formPayload
        });
        if (!uploadRes.ok) {
          toast.warning("Application submitted but resume upload failed. Please email your resume to Info@dsotocleaning.com");
        }
      }

      toast.success("Application received! Our recruitment team will contact you within 48 hours.");
      setFormData({ fullName: '', email: '', phone: '', city: '', position: '', availability: '', experience: '', message: '' });
      setResumeFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch {
      toast.error("Something went wrong. Please try again or email Info@dsotocleaning.com");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResumeChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowed.includes(file.type)) {
      toast.error("Please upload a PDF or Word document");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File must be under 10MB");
      return;
    }
    setResumeFile(file);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const benefits = [
    {
      icon: Award,
      title: "Competitive Work Environment",
      description: "Join a professional team operating at Fortune 500 standards"
    },
    {
      icon: TrendingUp,
      title: "Growth Opportunities",
      description: "Structured career advancement within a rapidly expanding organization"
    },
    {
      icon: Shield,
      title: "Structured Operational Systems",
      description: "Work within documented processes ensuring consistent execution"
    },
    {
      icon: Clock,
      title: "24/7 Flexible Shifts",
      description: "Multiple shift options accommodating diverse scheduling needs"
    },
    {
      icon: Users,
      title: "Professional Corporate Culture",
      description: "Enterprise-level workplace standards and operational discipline"
    }
  ];

  return (
    <div className="page-wrapper">
      <SEO 
        title={t('seo.careers.title')}
        description={t('seo.careers.description')}
        keywords={t('seo.careers.keywords')}
        canonical="https://dsotocleaning.com/careers"
      />
      
      <Navigation />
      
      <section className="careers-hero">
        <div className="hero-video-container">
          <iframe
            className="hero-video-iframe"
            src="https://www.youtube.com/embed/s9uJfxwZ9lU?autoplay=1&mute=1&controls=0&loop=1&playlist=s9uJfxwZ9lU&modestbranding=1&playsinline=1&rel=0"
            title="D Soto Cleaning Careers"
            allow="autoplay; fullscreen"
            allowFullScreen
          />
        </div>
        <div className="hero-overlay"></div>
        <div className="careers-hero-content">
          <h1 className="careers-hero-title">{t('careers.hero_title')}</h1>
          <p className="careers-hero-subtitle">{t('careers.hero_desc')}</p>
        </div>
      </section>

      <section className="section-padding">
        <SectionBubbles />
        <div className="container">
          <div className="careers-about">
            <h2 className="section-title text-center">About Working at D Soto Cleaning</h2>
            <div className="careers-content">
              <p className="careers-description">
                D Soto Cleaning operates in high-performance airport and hospitality environments. Our team members 
                are part of a structured, disciplined workforce delivering mission-critical fleet services 24 hours 
                a day, 7 days a week.
              </p>
              <div className="careers-looking-for">
                <h3 className="careers-subtitle">We are looking for:</h3>
                <ul className="careers-list">
                  <li>Detail-oriented professionals</li>
                  <li>Reliable team players</li>
                  <li>Operationally disciplined individuals</li>
                  <li>Growth-driven candidates</li>
                  <li>Individuals ready to work in fast-paced environments</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-light-gray">
        <div className="container">
          <h2 className="section-title text-center">Why Work With Us</h2>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <Card key={index} className="benefit-card">
                  <CardContent className="benefit-card-content">
                    <div className="benefit-icon-wrapper">
                      <IconComponent size={36} />
                    </div>
                    <h3 className="benefit-title">{benefit.title}</h3>
                    <p className="benefit-description">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-padding">
        <SectionBubbles />
        <div className="container">
          <h2 className="section-title text-center">Apply Now</h2>
          <p className="section-description text-center centered-text mb-12">
            Submit your application to join our professional workforce
          </p>
          <div className="careers-form-container">
            <form onSubmit={handleSubmit} className="careers-form">
              <div className="form-row">
                <Input
                  name="fullName"
                  placeholder="Full Name *"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
                <Input
                  name="email"
                  type="email"
                  placeholder="Email Address *"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-row">
                <Input
                  name="phone"
                  type="tel"
                  placeholder="Phone Number *"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
                <Input
                  name="city"
                  placeholder="City *"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-row">
                <Input
                  name="position"
                  placeholder="Position Applying For *"
                  value={formData.position}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
                <select
                  name="availability"
                  value={formData.availability}
                  onChange={handleChange}
                  required
                  className="form-select"
                >
                  <option value="">Availability *</option>
                  <option value="day">Day Shift</option>
                  <option value="night">Night Shift</option>
                  <option value="flexible">Flexible</option>
                </select>
              </div>
              <Input
                name="experience"
                placeholder="Years of Experience *"
                value={formData.experience}
                onChange={handleChange}
                required
                className="form-input"
              />
              <div className="resume-upload-section" data-testid="resume-upload">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleResumeChange}
                  accept=".pdf,.doc,.docx"
                  style={{ display: 'none' }}
                  data-testid="resume-file-input"
                />
                {resumeFile ? (
                  <div className="resume-file-display">
                    <FileText size={20} />
                    <span className="resume-filename">{resumeFile.name}</span>
                    <button type="button" onClick={() => { setResumeFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} className="resume-remove-btn" data-testid="resume-remove-btn">
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <button type="button" onClick={() => fileInputRef.current?.click()} className="resume-upload-btn" data-testid="resume-upload-btn">
                    <Upload size={20} />
                    <span>Upload Resume (PDF or Word, max 10MB)</span>
                  </button>
                )}
              </div>
              <Textarea
                name="message"
                placeholder="Tell us about your background and why you'd be a great fit..."
                value={formData.message}
                onChange={handleChange}
                rows={4}
                className="form-input"
              />
              <Button type="submit" size="lg" className="submit-button-full" disabled={isSubmitting} data-testid="careers-submit-btn">
                {isSubmitting ? <><Loader2 className="mr-2 animate-spin" size={18} /> Submitting...</> : <>Submit Your Application <ArrowRight className="ml-2" /></>}
              </Button>
            </form>
          </div>
        </div>
      </section>

      <section className="section-padding bg-corporate-blue">
        <div className="container">
          <h2 className="section-title text-white text-center">Our Team in Action</h2>
          <div className="careers-team-showcase">
            <img 
              src="https://customer-assets.emergentagent.com/job_dsoto-corporate/artifacts/rgxbkgu8_2.jpg" 
              alt="D Soto Cleaning Professional Team" 
              className="team-showcase-image"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Careers;
